import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../constants';

export type BillingPlanDto = {
  id: string;
  planKey: string;
  billingMode: 'SUBSCRIPTION';
  displayName: string;
  description: string | null;
  amountMinor: number;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  stripePriceId: string | null;
  lastSyncedAt: string | null;
  /** AI Pod plans only: free-trial days offered on first subscription. */
  trialDays?: number;
  /** AI Pod plans only: every interval the plan is sold at (the flat fields hold the primary one). */
  pricingIntervals?: Array<{
    interval: string;
    amountMinor: number;
    currency: string;
    stripePriceId?: string | null;
  }> | null;
};

export type BillingPlansResponse = {
  agentType: { id: string; code: string; name: string };
  plans: BillingPlanDto[];
};

export type CheckoutSessionResponse = {
  checkoutUrl: string | null;
  sessionId: string | null;
  paymentId: string | null;
  /** AI Pod checkout only: true when an existing subscription was switched in place (no checkout page). */
  planChanged?: boolean;
};

export type EntitlementDto = {
  id: string;
  userId: string;
  tenantId: string;
  agentTypeId: string | null;
  aiPodId?: string | null;
  sourceType: 'PAYMENT' | 'SUBSCRIPTION';
  status: string;
  validUntil: string | null;
  agentType: { id: string; code: string; name: string; shortDescription: string | null } | null;
  aiPod?: { id: string; code: string; name: string; shortDescription: string | null } | null;
  /** Set once an AiPod entitlement's graph has been materialized into a real tenant Workflow. */
  workflowId?: string | null;
  subscription: {
    billingPlanId: string | null;
    billingPlan: { id: string; planKey: string; displayName: string } | null;
    aiPodBillingPlanId?: string | null;
    aiPodBillingPlan?: { id: string; planKey: string; displayName: string } | null;
  } | null;
  payment: {
    billingPlanId: string | null;
    billingPlan: { id: string; planKey: string; displayName: string } | null;
    aiPodBillingPlanId?: string | null;
    aiPodBillingPlan?: { id: string; planKey: string; displayName: string } | null;
  } | null;
};

export type PendingPaymentDto = {
  id: string;
  billingPlan: {
    id: string;
    agentTypeId: string;
    planKey: string;
    displayName: string;
  } | null;
  aiPodBillingPlan?: {
    id: string;
    aiPodId: string;
    planKey: string;
    displayName: string;
  } | null;
};

export type MyBillingAccessResponse = {
  entitlements: EntitlementDto[];
  /** Present on newer API; treat as [] if missing. */
  pendingPayments?: PendingPaymentDto[];
};

export const billingService = {
  async getMyBillingAccess(): Promise<MyBillingAccessResponse> {
    const result = await apiClient.get<MyBillingAccessResponse | { data: MyBillingAccessResponse }>(
      API_ENDPOINTS.BILLING.ENTITLEMENTS,
      { authenticate: true }
    );
    const raw = (result as { data?: MyBillingAccessResponse }).data ?? (result as MyBillingAccessResponse);
    return {
      entitlements: raw?.entitlements ?? [],
      pendingPayments: raw?.pendingPayments ?? [],
    };
  },

  async listBillingPlans(agentTypeId: string): Promise<BillingPlansResponse> {
    const url = API_ENDPOINTS.BILLING.PLANS(agentTypeId);
    const result = await apiClient.get<BillingPlansResponse | { data: BillingPlansResponse }>(url, {
      authenticate: true,
    });
    const data = (result as { data?: BillingPlansResponse }).data ?? (result as BillingPlansResponse);
    return data;
  },

  async createCheckoutSession(payload: {
    billingPlanId: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<CheckoutSessionResponse> {
    const result = await apiClient.post<CheckoutSessionResponse | { data: CheckoutSessionResponse }>(
      API_ENDPOINTS.BILLING.CHECKOUT_SESSION,
      payload,
      { authenticate: true }
    );
    return (result as { data?: CheckoutSessionResponse }).data ?? (result as CheckoutSessionResponse);
  },
};
